import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// components
import { Button } from 'dibs-elements/exports/Button';
import { Toast } from 'dibs-toaster/exports/Toast';
import { FormattedMessage } from 'react-intl';
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import CheckmarkIcon from 'dibs-vg/dist/react/checkmark';

// helpers
import serverVars from 'server-vars';

// styles
import styles from './styles/TileOptions.scss';

export const { TOAST, TOOLTIP } = { TOAST: 'TOAST', TOOLTIP: 'TOOLTIP' };

const TileOptionsContent = ({ copyToClipboard, isCopied, reasonMessage }) => (
    // stop click propagation because otherwise Toast dismisses on click
    <div onClick={e => e.stopPropagation()}>
        <div className={styles.share}>
            <Button size="small" onClick={copyToClipboard} className={styles.copyButton}>
                {isCopied ? (
                    <div className={styles.copy}>
                        <CheckmarkIcon className={styles.checkmark} />
                        <FormattedMessage id="discover.feedTile.copied" defaultMessage="COPIED" />
                    </div>
                ) : (
                    <div className={styles.copy}>
                        <FormattedMessage
                            id="discover.feedTile.copyLink"
                            defaultMessage="COPY LINK"
                        />
                    </div>
                )}
            </Button>
        </div>
        <div className={styles.explanation}>
            <div className={styles.question}>
                <FormattedMessage
                    id="discover.feedTile.reasonQuestion"
                    defaultMessage="Why am I seeing this?"
                />
            </div>
            <div className={styles.reason}>{reasonMessage}</div>
        </div>
    </div>
);
TileOptionsContent.propTypes = {
    copyToClipboard: PropTypes.func.isRequired,
    isCopied: PropTypes.bool.isRequired,
    reasonMessage: PropTypes.string.isRequired,
};

export const TileOptions = ({ ctaUrl, reasonMessage, optionsType = TOOLTIP }) => {
    const [showTileOptionsMenu, setShowTileOptionsMenu] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [isCopied, setIsCopied] = useState(false);

    const toggleTileOptionsMenu = e => {
        if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
        }
        setShowTileOptionsMenu(!showTileOptionsMenu);
        setShowToast(optionsType === TOAST); // when showing Toast, allow Toast itself to toggle false to allow dismissal animation
    };

    const copyToClipboard = url => {
        const temp = document.createElement('input');
        temp.setAttribute('value', url);
        if (document.body) {
            // the flow type of body is HTMLBodyElement or null
            document.body.appendChild(temp);
        }
        temp.select();
        document.execCommand('copy');
        if (document.body) {
            // the flow type of body is HTMLBodyElement or null
            document.body.removeChild(temp);
        }

        setIsCopied(true);
    };

    useEffect(() => {
        const hideTileOptionsMenu = () => setShowTileOptionsMenu(false);
        if (showToast) {
            window.addEventListener('scroll', hideTileOptionsMenu);
        }
        return function removeOnScroll() {
            if (showToast) {
                window.removeEventListener('scroll', hideTileOptionsMenu); // only remove if it was added
            }
        };
    }, [showToast]);

    useEffect(() => {
        if (isCopied) {
            setTimeout(() => {
                setIsCopied(false);
            }, 2000);
        }
    }, [isCopied]);

    const copyLink = showTileOptionsMenu && `${serverVars.get('localeOrigin')}${ctaUrl}`;

    if (!ctaUrl || !reasonMessage) {
        return null;
    }

    return (
        <div className={styles.options} onClick={e => toggleTileOptionsMenu(e)}>
            <div className={styles.dot} />
            <div className={styles.dot} />
            <div className={styles.dot} />
            {showToast && (
                // setShowToast(false) to unmount the toast only after it has finished dismissal animation (triggered by isVisible=false)
                <Toast onClose={() => setShowToast(false)} isVisible={showTileOptionsMenu}>
                    <TileOptionsContent
                        copyToClipboard={() => copyToClipboard(copyLink)}
                        isCopied={isCopied}
                        reasonMessage={reasonMessage}
                    />
                </Toast>
            )}
            {optionsType === TOOLTIP && showTileOptionsMenu && (
                <Tooltip
                    align="left"
                    direction="bottom"
                    closeOnOutsideClick
                    isVisible
                    keepInViewport
                >
                    <div className={styles.tooltip}>
                        <TileOptionsContent
                            copyToClipboard={() => copyToClipboard(copyLink)}
                            isCopied={isCopied}
                            reasonMessage={reasonMessage}
                        />
                    </div>
                </Tooltip>
            )}
        </div>
    );
};

TileOptions.propTypes = {
    ctaUrl: PropTypes.string,
    reasonMessage: PropTypes.string,
    optionsType: PropTypes.oneOf([TOAST, TOOLTIP]),
};
